import { plural } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import { Plus } from "lucide-react";
import { observer } from "mobx-react-lite";
import { useState } from "react";

import { apiDeleteClinicsUuid } from "../../../api";
import { AppModel } from "../../../app/app-model";
import { useCountries } from "../../../catalogs/countries";
import { Button } from "../../../components/ui-kit/button";
import { DataTable } from "../../../components/ui-kit/tables/data-table";
import {
  getCountryOptions,
  getRegionOptions,
  regionDisabled,
  regionPredicate,
} from "../../../utils/filter-presets";
import { assert } from "../../../utils/utils";
import { AddClinicDialog } from "./add-clinic-dialog";
import { EditClinicDialog } from "./edit-clinic-dialog";

export const ClinicsRoute = observer(function ClinicsRoute(props: {
  app: AppModel;
}) {
  const { t } = useLingui();
  const admin = props.app.requireAdminShit();
  const model = admin.clinics;
  assert(model, "Clinics model not initialized");
  const countries = useCountries();

  const [showAddDialog, setShowAddDialog] = useState(false);

  return (
    <>
      {showAddDialog && (
        <AddClinicDialog
          admin={admin}
          onClose={() => setShowAddDialog(false)}
          onSaved={() => {
            void admin.refreshClinics();
          }}
        />
      )}

      <DataTable
        persistState
        editMode="dialog"
        itemKeyField="uuid"
        filterNoun={(count) =>
          t`${plural(count, { one: "clinic", other: "clinics" })}`
        }
        filterActions={
          <Button
            variant="primary"
            icon={Plus}
            onClick={() => setShowAddDialog(true)}
          >
            <Trans>New clinic</Trans>
          </Button>
        }
        columns={[
          {
            header: <Trans>Name</Trans>,
            attr: "name",
            type: "string",
            sort: true,
            initSort: "asc",
            filter: {
              kind: "text",
              group: t`Clinics`,
              placeholder: t`Search…`,
            },
            width: 200,
            cell: ({ item }) => <strong>{item.name}</strong>,
          },
          {
            header: <Trans>Ambulance</Trans>,
            attr: "ambulance",
            type: "string",
          },
          {
            header: <Trans>Address</Trans>,
            attr: "address",
            type: "string",
          },
          {
            header: <Trans>Country</Trans>,
            attr: "country",
            type: "enum",
            values: countries.countriesEnum,
            filter: {
              kind: "multi",
              group: t`Location`,
              options: getCountryOptions,
            },
            cell: ({ item }) => item.country,
          },
          {
            header: <Trans>Region</Trans>,
            attr: "region",
            filter: {
              kind: "multi",
              group: t`Location`,
              options: (table) => getRegionOptions(table, countries),
              disabled: regionDisabled,
              disabledMessage: t`Select a country first`,
              predicate: regionPredicate,
            },
            cell: ({ item }) => {
              const regions = countries.getRegionsEnum(item.country);
              return regions[item.region as number] || " ";
            },
          },
          {
            header: <Trans>Phone</Trans>,
            attr: "phone",
            type: "string",
            copyable: true,
          },
          {
            header: <Trans>Email</Trans>,
            attr: "email",
            type: "string",
            copyable: true,
          },
          {
            header: <Trans># of physicians</Trans>,
            attr: "physicians",
            width: 30,
            cell: ({ item }) => item.numPhysicians,
          },
        ]}
        renderEditDialog={({ item, onClose, onSave }) => (
          <EditClinicDialog
            item={item}
            admin={admin}
            onClose={onClose}
            onSave={onSave}
          />
        )}
        // renderEditDialog's onSave routes its patch through onEdit
        onEdit={(editedField, item) =>
          admin.updateClinic(item.uuid, editedField)
        }
        onDelete={async (item) => {
          await apiDeleteClinicsUuid({ path: { uuid: item.uuid } });
          await admin.refreshClinics();
        }}
        model={model}
        rowsPerPage={10}
      />
    </>
  );
});
